/**
 * The client's categories in the journal (boona docs/codebase/clients/README.md §3.6, owner
 * 2026-10-08): the salon's categories from Client settings with their current names and
 * colours, ticked on and off right here. A field of the visit's form like the client's name or
 * phone: the pick goes out with Save as `client.category_ids` and the backend records it in the
 * visit's change history (who, from what to what - owner 2026-10-10). Without the edit right (or
 * without a client) it only shows them. GET /api/v1/company/clients/categories.
 */
import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { Check, ChevronDown, Plus } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { apiClient } from "@/lib/apiClient"
import { goToDashboard } from "@/features/sso/goToDashboard"
import { type Category, fromPayload, toggledIds } from "@/lib/clients/categories"

function Pill({ c }: { c: Category }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-gray-200">
      <span className="h-2 w-2 rounded-full" style={{ background: c.color || "#9aa0a8" }} />
      {c.title}
    </span>
  )
}

export function ClientCategoryPicker({
  clientId,
  initial,
  value,
  onChange,
}: {
  clientId: number | null
  initial: any[]
  /** the form's pick (`client.category_ids`) once the picker was used; undefined = as loaded */
  value?: number[]
  /** the ticked category ids, for the form; saved with the visit */
  onChange: (ids: number[]) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const { data } = useQuery({
    queryKey: ["clientCategories"],
    queryFn: async () =>
      (await apiClient.get<{ categories: Category[]; can_edit: boolean }>("/api/v1/company/clients/categories")).data,
    staleTime: 60_000,
    retry: false,
  })
  const canEdit = !!clientId && !!data?.can_edit
  const all = data?.categories || []
  const loaded = fromPayload(initial)
  const ids = value ?? loaded.map((c) => c.id)
  // in the salon's order, with the names and colours it has now (the visit's copy until loaded)
  const selected = (all.length ? all : loaded).filter((c) => ids.includes(c.id))
  const chosen = new Set(ids)
  const toggle = (id: number) => onChange(toggledIds(ids, id))

  const pills = selected.length ? (
    <span className="flex flex-wrap gap-1.5">{selected.map((c) => <Pill key={c.id} c={c} />)}</span>
  ) : (
    <span className="text-xs text-gray-400">{t("appointment.client.category_none", "No category")}</span>
  )

  if (!canEdit) {
    return <div className="flex min-h-9 items-center rounded-md border border-gray-200 bg-gray-50/50 px-3 py-1.5">{pills}</div>
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex min-h-9 w-full items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-left hover:border-gray-400"
          aria-label={t("appointment.client.category", "Category")}
        >
          {pills}
          <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-1" align="start">
        {all.length === 0 ? (
          <div className="space-y-2 p-2 text-sm text-gray-600">
            <p>{t("appointment.client.category_empty", "No categories yet. Create them in Client settings.")}</p>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
              onClick={() => void goToDashboard("/dashboard/clients/settings")}
            >
              <Plus className="h-3.5 w-3.5" />
              {t("appointment.client.category_settings", "Client settings")}
            </button>
          </div>
        ) : (
          <ul className="max-h-64 overflow-y-auto">
            {all.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => toggle(c.id)}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-gray-50"
                >
                  <span className="flex h-4 w-4 items-center justify-center rounded border border-gray-300">
                    {chosen.has(c.id) && <Check className="h-3 w-3" />}
                  </span>
                  <span className="h-2 w-2 rounded-full" style={{ background: c.color || "#9aa0a8" }} />
                  <span className="truncate">{c.title}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  )
}
